/**
 * Qualifier Management Component
 * Provides an interface for managing qualifiers in the LIMS system
 */

import { useState } from 'react';
import { FaPlus, FaFilter, FaSync } from 'react-icons/fa';
import { useQualifiers } from '../../hooks/useQualifiers';
import QualifierList from './QualifierList';
import QualifierModal from './QualifierModal';
import styles from './QualifierManagement.module.css';

const QualifierManagement = () => {
    const [filterType, setFilterType] = useState('all');
    const [editingQualifier, setEditingQualifier] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const {
        qualifiers,
        loading,
        error,
        refresh,
        createQualifier,
        updateQualifier,
        deleteQualifier,
        toggleQualifierActive
    } = useQualifiers(filterType);

    const handleCreateNew = () => {
        setEditingQualifier(null);
        setShowModal(true);
    };

    const handleEdit = (qualifier) => {
        setEditingQualifier(qualifier);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditingQualifier(null);
    };

    const handleSuccess = () => {
        setShowModal(false);
        setEditingQualifier(null);
        refresh();
    };

    return (
        <div className={styles.qualifierManagement}>
            <div className={styles.header}>
                <h2>Qualifier Management</h2>
                <p className={styles.subtitle}>
                    Manage qualifiers for sample categorization and tracking
                </p>
            </div>

            <div className={styles.listView}>
                <div className={styles.listControls}>
                    <div className={styles.filterGroup}>
                        <FaFilter className={styles.filterIcon} />
                        <label>Filter:</label>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className={styles.filterSelect}
                        >
                            <option value="all">All Qualifiers</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                    </div>
                    <div className={styles.buttonGroup}>
                        <button
                            onClick={refresh}
                            className={styles.refreshButton}
                            disabled={loading}
                            title="Refresh"
                        >
                            <FaSync className={loading ? styles.spinning : ''} />
                        </button>
                        <button
                            onClick={handleCreateNew}
                            className={styles.createButton}
                        >
                            <FaPlus /> New Qualifier
                        </button>
                    </div>
                </div>

                <QualifierList
                    qualifiers={qualifiers}
                    loading={loading}
                    error={error}
                    onEdit={handleEdit}
                    onDelete={deleteQualifier}
                    onToggleActive={toggleQualifierActive}
                />
            </div>

            <QualifierModal
                isOpen={showModal}
                qualifier={editingQualifier}
                onClose={handleModalClose}
                onSuccess={handleSuccess}
                createQualifier={createQualifier}
                updateQualifier={updateQualifier}
            />
        </div>
    );
};

export default QualifierManagement;
